import { fingerprint } from "@foreman/crypto";
import { fromBase64, toBase64 } from "@foreman/protocol";

/** A computer this phone is paired with. Holds public information only. */
export interface ComputerRecord {
  /** Fingerprint of the computer's public key (stable; also what is shown as the short fingerprint). */
  id: string;
  /** Name chosen on this phone. The pairing code does not carry one. */
  label: string;
  relayUrl: string;
  /** base64 */
  daemonPublicKey: string;
  /** This phone's id in the computer's device list (used to remove itself). */
  deviceId: string;
  pairedAt: number;
}

export const computerIdFor = (daemonPublicKey: Uint8Array) => fingerprint(daemonPublicKey);
export const shortFingerprint = (c: Pick<ComputerRecord, "id">) => c.id.slice(0, 8);
export const publicKeyOf = (c: Pick<ComputerRecord, "daemonPublicKey">) => fromBase64(c.daemonPublicKey);

/** Synchronous string storage (localStorage-shaped), so the store works in browsers, React Native and tests. */
export interface KeyValue {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export class MemoryKeyValue implements KeyValue {
  private m = new Map<string, string>();
  get(k: string) {
    return this.m.get(k) ?? null;
  }
  set(k: string, v: string) {
    this.m.set(k, v);
  }
  remove(k: string) {
    this.m.delete(k);
  }
}

const KEY = "foreman.computers.v1";

export class ComputersStore {
  private computers: ComputerRecord[];
  private readonly listeners = new Set<() => void>();

  constructor(private readonly kv: KeyValue) {
    this.computers = this.load();
  }

  list(): ComputerRecord[] {
    return this.computers;
  }
  get(id: string): ComputerRecord | undefined {
    return this.computers.find((c) => c.id === id);
  }

  /** Add a computer, or replace it if this phone was already paired with it (re-pairing). */
  add(rec: { label: string; relayUrl: string; daemonPublicKey: Uint8Array; deviceId: string; pairedAt?: number }): ComputerRecord {
    const id = computerIdFor(rec.daemonPublicKey);
    const next: ComputerRecord = {
      id,
      label: rec.label.trim() || "My computer",
      relayUrl: rec.relayUrl,
      daemonPublicKey: toBase64(rec.daemonPublicKey),
      deviceId: rec.deviceId,
      pairedAt: rec.pairedAt ?? Date.now(),
    };
    this.commit([...this.computers.filter((c) => c.id !== id), next]);
    return next;
  }

  rename(id: string, label: string): void {
    const v = label.trim();
    if (!v) return;
    this.commit(this.computers.map((c) => (c.id === id ? { ...c, label: v } : c)));
  }

  remove(id: string): void {
    this.commit(this.computers.filter((c) => c.id !== id));
  }

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  private commit(next: ComputerRecord[]): void {
    this.computers = next;
    try {
      this.kv.set(KEY, JSON.stringify(next));
    } catch {
      /* storage can be unavailable (private mode); the list still works for this session */
    }
    for (const l of [...this.listeners]) l();
  }

  private load(): ComputerRecord[] {
    try {
      const raw = this.kv.get(KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as ComputerRecord[];
      return Array.isArray(parsed) ? parsed.filter((c) => c && typeof c.id === "string" && typeof c.daemonPublicKey === "string") : [];
    } catch {
      return [];
    }
  }
}
